import { defineStore } from "pinia";

import { pathOr } from "ramda";
import { i18n, PREFIX } from "../../i18n.js";

import { useChangeOptionsStore } from "@/stores/modules/changeOptions";
import { useUploadsStore } from "@/stores/modules/uploads";
import { useWebproductStore } from "@/stores/modules/webproduct";
import { useCustomStore } from "@/stores/modules/custom";
import { usePersonalizeStore } from "@/stores/modules/personalize";

export const useBundleStore = defineStore("bundle", {
  state: () => ({
    isActive: "",
    ppData: {
      //ppCode : {isMain:true, isEnabled:true, name:Name, isOpen:false, isComplete, type, template, excel}
    }
  }),

  getters: {
    getBundleIsPdfVtComplete(state) {
      const uploadsStore = useUploadsStore();
      //check if we have uploaded the pdfvt/excell file
      const uploadsPP = uploadsStore.getUploadsForPP;
      if (uploadsPP.includes(this.getBundleMainProductPart)) {
        return true;
      }
      return false;
    },
    getBundleIsPdfVt(state) {
      //if the main file is excel
      for (var pp in state.ppData) {
        if (state.ppData[pp]["isMain"] && state.ppData[pp]["excel"]) {
          return true;
        }
      }

      return false;
    },
    getBundlePdfVtFile(state) {
      const uploadsStore = useUploadsStore();
      const excelPP = this.getBundlePdfVtProductPart;
      if (excelPP) {
        const excelPath =
          uploadsStore.getUploadRelativePathForProductPart(excelPP);
        if (excelPath) {
          return excelPath;
        }
      }
      return null;
    },
    getBundlePdfVtProductPart(state) {
      for (var pp in state.ppData) {
        if (state.ppData[pp]["isMain"] && state.ppData[pp]["excel"]) {
          return pp;
        }
      }

      return null;
    },
    getBundleProductPartType(state) {
      if (this.getBundleIsActive) {
        return "appProductPartsBundle";
      }
      return "appProductParts";
    },
    getBundleIsActive(state) {
      const webproductStore = useWebproductStore();
      return webproductStore.getConfig.bundle_type
        ? parseInt(webproductStore.getConfig.bundle_type)
        : 0;
    },
    getBundleMainProductPart(state) {
      const webproductStore = useWebproductStore();
      if (!this.getBundleIsActive) {
        return false;
      }
      return webproductStore.getConfig.bundle_main_product_part;
    },
    getBundlePPData(state) {
      return function (ppcode) {
        return state.ppData[ppcode];
      };
    },
    getBundleClass(state) {
      return this.getBundleIsActive ? "matrix_layoutMainBundle" : "";
    },
    getBundleProductPartCodes(state) {
      return [
        this.getBundleMainProductPart,
        ...Object.keys(state.ppData).filter(el => {
          return el != this.getBundleMainProductPart;
        })
      ];
    },
    getBundleIsActiveProductPart(state) {
      const that = this;
      return function (ppcode) {
        if (!that.getBundleIsActive) {
          return true;
        }
        return state.ppData[ppcode].isEnabled;
      };
    },
    getBundleTheActiveProductPart(state) {
      if (!this.getBundleIsActive) {
        return false;
      }
      for (var pp in state.ppData) {
        if (state.ppData[pp]["isOpen"]) {
          return pp;
        }
      }
      return false;
    },
    getBundleAllActiveProductParts(state) {
      const data = [];
      data.push({
        text: i18n.t(
          PREFIX.PP + state.ppData[this.getBundleMainProductPart].name
        ),
        id: this.getBundleMainProductPart,
        type: state.ppData[this.getBundleMainProductPart].type,
        excel: state.ppData[this.getBundleMainProductPart].excel
      });

      for (var pp in state.ppData) {
        if (state.ppData[pp]["isEnabled"] && !state.ppData[pp]["isMain"]) {
          data.push({
            text: i18n.t(PREFIX.PP + state.ppData[pp].name),
            id: pp,
            type: state.ppData[pp].type,
            excel: state.ppData[pp].excel
          });
        }
      }

      return data;
    },
    getBundleMatrixComplete(state) {
      return function (payload) {
        return state.ppData[payload].isComplete;
      };
    },
    getBundleTemplatePP(state) {
      return function (pp) {
        return state.ppData[pp].template;
      };
    },
    getBundleAllActiveProductPartsCodes(state) {
      const pps = this.getBundleAllActiveProductParts;
      const data = [];
      for (var pp in pps) {
        data.push(pps[pp].id);
      }

      return data;
    },
    getBundleShowAddToCart(state) {
      const personalizeStore = usePersonalizeStore();
      const uploadsStore = useUploadsStore();
      const ppWithPers = personalizeStore.getPPWithCustomizedProductId;
      const ppWithUploads = uploadsStore.getUploadsForPP;
      const ppChecked = this.getBundleAllActiveProductPartsCodes;

      const missing = ppChecked.filter(el => {
        return !ppWithPers.includes(el) && !ppWithUploads.includes(el);
      });

      return missing.length == 0;
    }
  },

  actions: {
    setBundleIsActive(payload) {
      this.isActive = payload;
    },
    setBundlePPData(payload) {
      const webproductStore = useWebproductStore();
      const data = {};
      for (var i in webproductStore.getProductPartsNames) {
        const item = webproductStore.getProductPartsNames[i];
        const ppData = webproductStore.getProductPartByCode(item.code);
        const type = pathOr("upload", ["attributes", "bundle_type"], ppData[0]);
        let template = 0;
        let excel = false;
        if (type == "personalize") {
          template = pathOr(
            0,
            ["attributes", "bundle_personalization_id"],
            ppData[0]
          );
        } else if (type == "upload_excel") {
          excel = true;
        }
        data[item.code] = {
          name: item.label,
          isMain: false,
          isEnabled: false,
          isOpen: false,
          isComplete: false,
          type,
          template,
          excel
        };
        if (item.code == this.getBundleMainProductPart) {
          data[item.code]["isMain"] = true;
          data[item.code]["isEnabled"] = true;
          data[item.code]["isOpen"] = true;
        }
      }
      this.ppData = data;
    },
    setBundleCheckPP(payload) {
      const changeOptionsStore = useChangeOptionsStore();
      const ppData = { ...this.ppData };
      if (payload.checked) {
        for (var pp in ppData) {
          if (ppData[pp].isOpen) {
            ppData[pp]["isOpen"] = false;
          }
        }
        ppData[payload.ppcode]["isEnabled"] = true;
        ppData[payload.ppcode]["isOpen"] = true;
      } else {
        // we unchecked it
        ppData[payload.ppcode]["isEnabled"] = false;
        if (ppData[payload.ppcode]["isOpen"]) {
          for (var pp in ppData) {
            if (ppData[pp].isMain) {
              ppData[pp]["isOpen"] = true;
              break;
            }
          }
        }
        ppData[payload.ppcode]["isOpen"] = false;
      }
      this.ppData = ppData;

      changeOptionsStore.changeOptions({});
    },
    setBundleOpenPP(payload) {
      const ppData = { ...this.ppData };
      for (var pp in ppData) {
        if (pp == payload) {
          ppData[pp]["isOpen"] = true;
        } else {
          ppData[pp]["isOpen"] = false;
        }
      }

      this.ppData = ppData;
    },
    setBundleMatrixComplete(payload) {
      const ppData = { ...this.ppData };
      ppData[payload].isComplete = true;
      this.ppData = ppData;
    },
    setBundleQuantity(payload) {
      const changeOptionsStore = useChangeOptionsStore();
      const customStore = useCustomStore();
      if (changeOptionsStore.getQuantitiesValues.includes("" + payload)) {
        const qtyItem = changeOptionsStore.getQuantitiesValuesWithCode.find(
          el => {
            return el.qty == payload;
          }
        );

        changeOptionsStore.setQuantity(qtyItem);
        return;
      }
      customStore.setCustomQuantity(payload);
    }
  }
});
